import type { ReactElement } from 'react';
import type { DocumentThemeSlug } from '@xdocs/schemas';
import { cn } from '@xdocs/ui';
import type { DocumentThemeVisual } from '../../features/reader/index.js';
import { useStyleT } from './useStyleT.js';
import styles from './ThemeCard.module.css';

export interface ThemeCardProps {
  slug: DocumentThemeSlug;
  visual: DocumentThemeVisual;
  selected: boolean;
  isCurrent: boolean;
  onSelect: (slug: DocumentThemeSlug) => void;
}

/** One gallery tile in the F65 `/style/:docId` Theme gallery — swatch
 * preview + name + PRO/"current" badges + one-line "intended for". */
export function ThemeCard({ slug, visual, selected, isCurrent, onSelect }: ThemeCardProps): ReactElement {
  const { t } = useStyleT();

  return (
    <button
      type="button"
      aria-pressed={selected}
      className={cn(styles['card'], selected && styles['cardSelected'])}
      onClick={() => onSelect(slug)}
    >
      <span className={cn(styles['swatch'])} style={{ background: visual.pageBackground, color: visual.bodyTextColor }} aria-hidden="true">
        <span className={cn(styles['swatchGlyph'])} style={{ fontFamily: visual.bodyFontKind === 'serif' ? 'var(--font-reader-serif)' : 'var(--font-sans)' }}>
          {/* i18n-exempt: decorative type-sample glyph (mirrors design/app/29-styling.html's swatch "Aa"), not real copy — aria-hidden on the parent, never announced */}
          Aa
          <span className={cn(styles['swatchDot'])} style={{ background: visual.accent }} />
        </span>
      </span>
      <span className={cn(styles['body'])}>
        <span className={cn(styles['nameRow'])}>
          <span className={cn(styles['name'])}>{t(`style:theme.${slug}.name`)}</span>
          {visual.pro && <span className={cn(styles['proBadge'])}>{t('style:badge.pro')}</span>}
          {isCurrent && <span className={cn(styles['currentBadge'])}>{t('style:badge.current')}</span>}
        </span>
        <span className={cn(styles['intended'])}>{t(`style:theme.${slug}.intended`)}</span>
      </span>
    </button>
  );
}
